import React, { useEffect, useMemo, useState } from 'react'
import {
    useLocation,
    useNavigate,
    useSearchParams,
    useParams,
} from 'react-router-dom'
import { Pages } from '../../../Pages'
import Button from '../../common/form/inputs/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPhone } from '@fortawesome/free-solid-svg-icons'
import LinkedInUrlHelp from '../LinkedInUrlHelp'
import {
    jsCapture,
    jsIdentify,
    setJsUserId,
    setJsUserEmail,
} from '../../../jobseeker/jobseekerPosthog'
import jobSeekerApi from '../../../api/jobSeeker/JobSeekerApi'
import kitApi from '../../../api/kit/KitApi'
import warmupApi from '../../../api/warmup/WarmupApi'
import { executeRecaptcha } from '../../../utils/recaptcha'
import {
    speakToKitSerifStyle,
    speakToKitThemeVars,
} from '../theme/speakToKitThemeVars'
import SpeakToKitLogo from '../components/SpeakToKitLogo'
import { buildTargetWithReturnPath } from '../utils/loginTarget'
import useSpeakToKitPageTitle from '../hooks/useSpeakToKitPageTitle'

type FormState = {
    name: string
    email: string
    phone: string
    linkedinUrl: string
    acceptedTerms: boolean
}

type TextField = Exclude<keyof FormState, 'acceptedTerms'>

type FormErrors = Partial<Record<keyof FormState, string>>

type JobDetails = {
    jobUrlId: string
    jobTitle: string
}

type JobLoadState = 'loading' | 'ready' | 'inactive' | 'not_found' | 'error'

const containerClass = 'mx-auto w-full max-w-[1200px] px-6 font-sans'

export default function CandidateApply(): React.JSX.Element {
    useSpeakToKitPageTitle('Apply')
    const navigate = useNavigate()
    const location = useLocation()
    const { jobUrlId: jobUrlIdParam } = useParams()
    const jobUrlId = (jobUrlIdParam ?? '').trim()
    const [searchParams] = useSearchParams()
    const prefillName = useMemo(
        () =>
            (
                searchParams.get('cand_name') ??
                searchParams.get('name') ??
                ''
            ).trim(),
        [searchParams],
    )
    const [formState, setFormState] = useState<FormState>({
        name: prefillName,
        email: (searchParams.get('cand_email') ?? '').trim(),
        phone: (searchParams.get('cand_phone') ?? '').trim(),
        linkedinUrl: (searchParams.get('cand_li_url') ?? '').trim(),
        acceptedTerms: false,
    })
    const [jobDetails, setJobDetails] = useState<JobDetails | null>(null)
    const [jobLoadState, setJobLoadState] = useState<JobLoadState>('loading')
    const [jobLoadError, setJobLoadError] = useState<string | null>(null)
    const [errors, setErrors] = useState<FormErrors>({})
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        jsCapture('jobseeker_apply_viewed')
        // jsCapture is a module import and stable
    }, [])

    useEffect(() => {
        let warmupTriggered = false
        let cancelled = false

        const triggerWarmup = async () => {
            if (warmupTriggered) {
                return
            }
            warmupTriggered = true
            try {
                const token = await executeRecaptcha('warmup')
                if (cancelled) {
                    return
                }
                const finalToken = token ?? 'local-warmup-token'
                await warmupApi.warmup(finalToken)
            } catch (error) {
                console.warn('Failed to warm up system', error)
            }
        }

        triggerWarmup()

        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (!jobUrlId) {
            setJobDetails(null)
            setJobLoadError(null)
            setJobLoadState('not_found')
            return
        }

        const controller = new AbortController()

        const loadJobDetails = async () => {
            setJobLoadState('loading')
            setJobLoadError(null)

            try {
                const res = await kitApi.getScreeningJobPublic(jobUrlId)
                const job = res.job

                if (!job) {
                    setJobDetails(null)
                    setJobLoadState('not_found')
                    return
                }

                setJobDetails({
                    jobUrlId,
                    jobTitle: job.jobTitle?.trim() || 'This role',
                })

                if (!job.isActive) {
                    setJobLoadState('inactive')
                    return
                }

                setJobLoadState('ready')
            } catch (error) {
                if (controller.signal.aborted) return
                console.error('Failed to load job register', error)
                setJobDetails(null)
                setJobLoadState('error')
                setJobLoadError(
                    error instanceof Error
                        ? error.message
                        : 'Unable to load this role right now.',
                )
            }
        }

        void loadJobDetails()

        return () => {
            controller.abort()
        }
    }, [jobUrlId])

    const handleChange =
        (field: TextField) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value
            setFormState((prev) => ({ ...prev, [field]: value }))
            setSubmitError(null)
            if (errors[field]) {
                setErrors((prev) => {
                    const next = { ...prev }
                    delete next[field]
                    return next
                })
            }
        }

    const handleCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const checked = event.target.checked
        setFormState((prev) => ({ ...prev, acceptedTerms: checked }))
        setSubmitError(null)
        if (errors.acceptedTerms) {
            setErrors((prev) => {
                const next = { ...prev }
                delete next.acceptedTerms
                return next
            })
        }
    }

    const validate = (
        name: string,
        email: string,
        phone: string,
        linkedinUrl: string,
        acceptedTerms: boolean,
    ): FormErrors => {
        const nextErrors: FormErrors = {}

        if (!name) {
            nextErrors.name = 'Name is required.'
        }

        if (!email) {
            nextErrors.email = 'Email is required.'
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            nextErrors.email = 'Enter a valid email address.'
        }

        if (!phone) {
            nextErrors.phone = 'Phone number is required.'
        }

        if (!linkedinUrl) {
            nextErrors.linkedinUrl = 'LinkedIn URL is required.'
        } else if (!linkedinUrl.toLowerCase().includes('linkedin.com/in')) {
            nextErrors.linkedinUrl =
                'Use your profile link that includes linkedin.com/in.'
        }

        if (!acceptedTerms) {
            nextErrors.acceptedTerms =
                'You must agree to the Terms and Conditions.'
        }

        return nextErrors
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!jobDetails || jobLoadState !== 'ready') {
            setSubmitError('We could not load this role. Please try again.')
            return
        }
        const trimmedName = formState.name.trim()
        const trimmedEmail = formState.email.trim()
        const trimmedPhone = formState.phone.trim()
        const trimmedLinkedIn = formState.linkedinUrl.trim()
        const acceptedTerms = formState.acceptedTerms
        const fieldsPresent = [
            trimmedName ? 'name' : null,
            trimmedEmail ? 'email' : null,
            trimmedPhone ? 'phone' : null,
            trimmedLinkedIn ? 'linkedinUrl' : null,
            acceptedTerms ? 'acceptedTerms' : null,
        ].filter(Boolean) as string[]

        jsCapture('jobseeker_apply_submitted', {
            fields_present: fieldsPresent,
        })

        const validationErrors = validate(
            trimmedName,
            trimmedEmail,
            trimmedPhone,
            trimmedLinkedIn,
            acceptedTerms,
        )
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors)
            return
        }

        setFormState({
            name: trimmedName,
            email: trimmedEmail,
            phone: trimmedPhone,
            linkedinUrl: trimmedLinkedIn,
            acceptedTerms,
        })
        setErrors({})
        setSubmitError(null)
        setIsSubmitting(true)
        try {
            const response = await jobSeekerApi.submitSignup({
                name: trimmedName,
                email: trimmedEmail,
                phone: trimmedPhone,
                linkedinUrl: trimmedLinkedIn,
                acceptedTerms,
                source: 'jobScreening',
                screeningJobUrlId: jobDetails.jobUrlId,
            })

            if (response.requiresLogin) {
                jsCapture('jobseeker_apply_login_required', {
                    email: trimmedEmail,
                })
                const target = buildTargetWithReturnPath(
                    `${Pages.CandidateApply}/${jobDetails.jobUrlId}/kit`,
                    `${location.pathname}${location.search}`,
                )
                navigate(
                    `${Pages.CandidateLogin}?email=${encodeURIComponent(
                        trimmedEmail,
                    )}&step=choose-method&target=${encodeURIComponent(target)}`,
                    { replace: true },
                )
                return
            }

            const userId =
                typeof response.userId === 'string'
                    ? response.userId.trim()
                    : ''

            if (!userId) {
                throw new Error('Signup succeeded but no user id was returned.')
            }

            jsIdentify(userId, { email: trimmedEmail })
            setJsUserId(userId)
            setJsUserEmail(trimmedEmail)
            jsCapture('jobseeker_apply_submission_succeeded', {
                user_id: userId,
            })
            navigate(`${Pages.CandidateApply}/${jobDetails.jobUrlId}/kit`)
        } catch (error) {
            console.error('Failed to submit jobseeker apply form', error)
            const errorMessage =
                error instanceof Error
                    ? error.message.slice(0, 120)
                    : 'request_failed'

            jsCapture('jobseeker_apply_submission_failed', {
                error_code: 'request_failed',
                error_message: errorMessage,
            })
            const message =
                error instanceof Error
                    ? error.message
                    : 'Unable to submit your details right now.'
            setSubmitError(message)
        } finally {
            setIsSubmitting(false)
        }
    }

    const displayJobTitle = jobDetails?.jobTitle.trim() || 'this role'

    const renderContent = () => {
        if (jobLoadState === 'loading') {
            return (
                <div className="mx-auto w-full max-w-2xl">
                    <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-[var(--shadow-card)] md:p-8">
                        <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                            Loading role details…
                        </div>
                    </div>
                </div>
            )
        }

        if (jobLoadState === 'not_found') {
            return (
                <div className="mx-auto w-full max-w-2xl">
                    <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-[var(--shadow-card)] md:p-8">
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                            404
                        </p>
                        <h2
                            className="mt-3 text-2xl font-semibold text-foreground"
                            style={speakToKitSerifStyle}
                        >
                            Role not found
                        </h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            We couldn&apos;t find this job link. Please check
                            the URL or contact the person who shared it.
                        </p>
                        <div className="mt-5 flex justify-center">
                            <Button
                                type="link"
                                style="secondary"
                                to={Pages.CandidateLandingOption2}
                                classes="border border-primary/40 text-primary px-6 py-2 hover:bg-primary/10"
                            >
                                Back to Speak To Kit
                            </Button>
                        </div>
                    </div>
                </div>
            )
        }

        if (jobLoadState === 'inactive') {
            return (
                <div className="mx-auto w-full max-w-2xl">
                    <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-[var(--shadow-card)] md:p-8">
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                            Inactive
                        </p>
                        <h2
                            className="mt-3 text-2xl font-semibold text-foreground"
                            style={speakToKitSerifStyle}
                        >
                            This role is no longer active
                        </h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            This screening link is no longer accepting new
                            applicants. Please contact the person who shared it
                            if you think this is a mistake.
                        </p>
                        <div className="mt-5 flex justify-center">
                            <Button
                                type="link"
                                style="secondary"
                                to={Pages.CandidateLandingOption2}
                                classes="border border-primary/40 text-primary px-6 py-2 hover:bg-primary/10"
                            >
                                Back to Speak To Kit
                            </Button>
                        </div>
                    </div>
                </div>
            )
        }

        if (jobLoadState === 'error') {
            return (
                <div className="mx-auto w-full max-w-2xl">
                    <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-[var(--shadow-card)] md:p-8">
                        <h2
                            className="text-2xl font-semibold text-foreground"
                            style={speakToKitSerifStyle}
                        >
                            Unable to load this role
                        </h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                            We&apos;re having trouble loading the job details.
                            Please refresh and try again.
                        </p>
                        {jobLoadError ? (
                            <p className="mt-3 text-xs text-muted-foreground">
                                Error: {jobLoadError}
                            </p>
                        ) : null}
                        <div className="mt-5 flex justify-center">
                            <Button
                                type="button"
                                style="secondary"
                                onClick={() => {
                                    window.location.reload()
                                }}
                                classes="border border-primary/40 text-primary px-6 py-2 hover:bg-primary/10"
                            >
                                Try again
                            </Button>
                        </div>
                    </div>
                </div>
            )
        }

        return (
            <div className="mx-auto w-full max-w-2xl">
                <form
                    className="space-y-6 rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] md:p-8"
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <div className="space-y-2">
                        <h1
                            className="text-2xl font-semibold text-foreground"
                            style={speakToKitSerifStyle}
                        >
                            {displayJobTitle}
                        </h1>
                    </div>
                    <div className="space-y-2">
                        <label
                            htmlFor="candidate-name"
                            className="text-sm font-semibold text-foreground"
                        >
                            Name
                        </label>
                        <input
                            id="candidate-name"
                            name="name"
                            type="text"
                            autoComplete="name"
                            value={formState.name}
                            onChange={handleChange('name')}
                            className="w-full rounded-xl border border-border bg-white px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                            placeholder="Jane Smith"
                            required
                        />
                        {errors.name && (
                            <p className="text-sm text-destructive">
                                {errors.name}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label
                            htmlFor="candidate-email"
                            className="text-sm font-semibold text-foreground"
                        >
                            Email
                        </label>
                        <input
                            id="candidate-email"
                            name="email"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            value={formState.email}
                            onChange={handleChange('email')}
                            className="w-full rounded-xl border border-border bg-white px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                            placeholder="you@example.com"
                            required
                        />
                        {errors.email && (
                            <p className="text-sm text-destructive">
                                {errors.email}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label
                            htmlFor="candidate-phone"
                            className="text-sm font-semibold text-foreground"
                        >
                            Phone
                        </label>
                        <input
                            id="candidate-phone"
                            name="phone"
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            value={formState.phone}
                            onChange={handleChange('phone')}
                            className="w-full rounded-xl border border-border bg-white px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                            placeholder="123 456 7890"
                            required
                        />
                        {errors.phone && (
                            <p className="text-sm text-destructive">
                                {errors.phone}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label
                            htmlFor="candidate-linkedin"
                            className="text-sm font-semibold text-foreground"
                        >
                            LinkedIn URL
                        </label>
                        <input
                            id="candidate-linkedin"
                            name="linkedinUrl"
                            type="url"
                            inputMode="url"
                            value={formState.linkedinUrl}
                            onChange={handleChange('linkedinUrl')}
                            className="w-full rounded-xl border border-border bg-white px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                            placeholder="https://www.linkedin.com/in/your-profile"
                            required
                        />
                        {errors.linkedinUrl && (
                            <p className="text-sm text-destructive">
                                {errors.linkedinUrl}
                            </p>
                        )}
                        <LinkedInUrlHelp />
                    </div>

                    <div className="space-y-2">
                        <label
                            htmlFor="candidate-terms"
                            className="flex items-start gap-3 text-sm text-foreground"
                        >
                            <input
                                id="candidate-terms"
                                name="acceptedTerms"
                                type="checkbox"
                                checked={formState.acceptedTerms}
                                onChange={handleCheckboxChange}
                                className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                                required
                            />
                            <span className="text-foreground">
                                I agree to the{' '}
                                <a
                                    href="https://speaktokit.com/privacy"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary underline"
                                >
                                    Terms and Conditions
                                </a>
                            </span>
                        </label>
                        {errors.acceptedTerms && (
                            <p className="text-sm text-destructive">
                                {errors.acceptedTerms}
                            </p>
                        )}
                    </div>

                    <Button
                        type="submit"
                        style="primary"
                        classes="w-full justify-center py-3 text-base bg-primary text-white shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:bg-primary/90 transition-all"
                        enabled={!isSubmitting && jobLoadState === 'ready'}
                    >
                        {!isSubmitting && (
                            <FontAwesomeIcon icon={faPhone} className="mr-2" />
                        )}
                        {isSubmitting ? 'Submitting…' : 'Speak to Kit'}
                    </Button>
                    {submitError && (
                        <p className="text-center text-sm text-destructive">
                            {submitError}
                        </p>
                    )}
                </form>
            </div>
        )
    }

    return (
        <div
            className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
            style={speakToKitThemeVars}
        >
            <header className="w-full pt-6">
                <div
                    className={`${containerClass} flex items-center justify-center`}
                >
                    <SpeakToKitLogo to={Pages.CandidateLandingOption2} />
                </div>
            </header>

            <main className="flex-1">
                <section className="py-8 md:py-12">
                    <div className={containerClass}>{renderContent()}</div>
                </section>
            </main>
        </div>
    )
}
